import React from 'react'
import { fmtWhen } from './reportShared'

// The report audit trail — a DISPLAY layer over history that was always
// written and never shown. Every source below (published_*, the three
// *_history arrays, the approval columns, pm_escalated_*, deleted_*) is
// already on the client: both /api/field-reports?action=list and ?action=get
// select('*'). NOTHING here writes, and NO schema change is implied.
//
// The approve path is deliberately NOT changed: reopen preserves
// previous_approver / previous_approver_role in its own history entry and the
// approval columns hold the LATEST approval, so an approve -> reopen ->
// approve chain is fully reconstructible without touching payroll-critical
// code.

// How a report came into existence (daily_field_reports.source).
const CREATED_BY = {
  scheduled: 'schedule published',
  ad_hoc: 'added by the office',
  adhoc: 'added by the office',
  inbound: 'foreman’s phone call',
}

// How a submission/edit reached us. Covers both the report-level
// foreman_submission_source and the per-entry foreman_edit_history source.
const SUBMIT_SOURCE = {
  inbound_call: 'by phone (Sarah)',
  edit_page: 'via the emailed link',
  foreman_edit_page: 'via the emailed link',
  app: 'in the app',
  foreman_app: 'in the app',
  pm_form_link: 'by the PM on the form link',
}

const PM_EDIT_SOURCE = {
  office_entry: 'office entry',
  pm_form_link: 'PM form link',
}

const arr = (v) => (Array.isArray(v) ? v : [])
const clean = (v) => (v == null || v === '' ? '' : String(v))

function joinDetail(...parts) {
  return parts.map(clean).filter(Boolean).join(' — ')
}

function fieldsText(fields) {
  const list = arr(fields).map(clean).filter(Boolean)
  return list.length ? `changed: ${list.join(', ')}` : ''
}

// Build the ordered trail. Entries with no timestamp sort LAST (they can't be
// placed, but must never be dropped).
export function buildAuditTrail(report) {
  const r = report || {}
  const out = []
  const push = (at, kind, label, who, detail) => {
    out.push({ at: at || null, kind, label, who: clean(who), detail: clean(detail) })
  }

  // Created
  if (r.published_at || r.published_by || r.source) {
    push(r.published_at, 'created', 'Report created', r.published_by, CREATED_BY[r.source] || '')
  }

  // First submission. Skipped when a foreman_edit_history entry sits within 5
  // seconds of it — the same event written twice would otherwise read as two.
  const foremanHist = arr(r.foreman_edit_history)
  if (r.foreman_first_submitted_at) {
    const firstMs = new Date(r.foreman_first_submitted_at).getTime()
    const dup = foremanHist.some((h) => {
      const t = h && h.timestamp ? new Date(h.timestamp).getTime() : NaN
      return Number.isFinite(t) && Math.abs(t - firstMs) <= 5000
    })
    if (!dup) {
      push(r.foreman_first_submitted_at, 'submitted', 'First submitted', r.foreman_name,
        SUBMIT_SOURCE[r.foreman_submission_source] || '')
    }
  }

  // Every foreman save IS a submission (the inbound-first rule).
  foremanHist.forEach((h) => {
    if (!h) return
    push(h.timestamp, 'submitted', 'Foreman submitted / resubmitted',
      h.by || r.foreman_name, SUBMIT_SOURCE[h.source] || '')
  })

  // PM / office edits.
  arr(r.pm_edit_history).forEach((h) => {
    if (!h) return
    const label = `Edited by ${clean(h.by) || 'the office'}${h.role ? ` (${h.role})` : ''}`
    push(h.at, 'pm_edit', label, h.by,
      joinDetail(PM_EDIT_SOURCE[h.source] || (h.source ? 'in-app edit' : ''), fieldsText(h.fields_changed)))
  })

  // Admin actions — reopen / approve-as-admin / edit / delete / restore, plus
  // any kind we don't know about (never dropped).
  arr(r.admin_actions_history).forEach((h) => {
    if (!h) return
    const who = h.by
    switch (h.action) {
      case 'reopened': {
        const prev = clean(h.previous_approver)
        const role = clean(h.previous_approver_role)
        const detail = joinDetail(
          h.previous_status ? `was ${h.previous_status}` : '',
          prev ? `approved by ${prev}${role ? ` (${role})` : ''}` : '',
        )
        push(h.at, 'reopened', 'Approval reopened', who, detail)
        break
      }
      case 'approved_as_admin':
        push(h.at, 'approved', 'Approved', who, h.role)
        break
      case 'edited':
        push(h.at, 'admin_edit', 'Edited (admin link)', who, fieldsText(h.fields_changed))
        break
      case 'deleted':
        push(h.at, 'deleted', 'Deleted', who, '')
        break
      case 'restored':
        push(h.at, 'restored', 'Restored', who, '')
        break
      case 'auto_linked_employee':
        // Written by the unlinked-worker hard stop (api/lib/unlinked-gate.js)
        // when a typed name matched exactly ONE employee at approval.
        push(h.at, 'other', 'Linked to payroll employee', who,
          Array.isArray(h.names) ? h.names.map(clean).filter(Boolean).join(', ') : '')
        break
      case 'approval_held_unlinked': {
        // Written when a submit-and-approve button submitted the report but
        // HELD the approval because a worker wasn't linked to a payroll
        // employee number (api/lib/unlinked-gate.js).
        const names = Array.isArray(h.names) ? h.names.map(clean).filter(Boolean).join(', ') : ''
        push(h.at, 'other', `Submitted - approval held: ${names || 'a worker'} not linked to a payroll employee`, who, '')
        break
      }
      case 'created_out_of_week': {
        // Stamped ONCE by api/field-reports create-adhoc, at the moment of
        // creation — never recomputed here (see outOfWeekEntry below).
        const wk = clean(h.payroll_week_start) && clean(h.payroll_week_end)
          ? `${clean(h.payroll_week_start)}–${clean(h.payroll_week_end)}`
          : ''
        push(h.at, 'out_of_week', 'Created for a date outside the payroll week', who,
          wk && clean(h.report_date) ? `${clean(h.report_date)} is outside ${wk}` : '')
        break
      }
      default:
        push(h.at, 'other', clean(h.action) || 'Action', who, '')
    }
  })

  // Escalation.
  if (r.pm_escalated_at) {
    push(r.pm_escalated_at, 'escalated', 'Escalated to the office', '',
      r.pm_escalated_count ? `escalation #${r.pm_escalated_count}` : '')
  }

  // The CURRENT approval (the columns hold the latest one).
  if (r.pm_approval_status === 'approved') {
    let detail = clean(r.approved_by_role)
    if (r.approval_via === 'escalation') {
      detail += ` — on behalf of ${clean(r.approved_on_behalf_of) || 'the PM'}`
    } else if (r.approval_via === 'pm_form_link') {
      detail += ' — one-touch finalize from the PM form link'
    }
    push(r.pm_approved_at, 'approved', 'Approved', r.pm_approved_by, detail.trim())
  }

  if (r.deleted_at || r.deleted_by) {
    push(r.deleted_at, 'deleted', 'Deleted', r.deleted_by, '')
  }

  // Ascending by time; undated entries last, in the order they were collected.
  return out
    .map((e, i) => ({ e, i, t: e.at ? new Date(e.at).getTime() : NaN }))
    .sort((a, b) => {
      const av = Number.isFinite(a.t), bv = Number.isFinite(b.t)
      if (av && bv) return a.t - b.t || a.i - b.i
      if (av) return -1
      if (bv) return 1
      return a.i - b.i
    })
    .map((x) => x.e)
}

// The NEWEST reopen, or null. Drives the always-visible amber note: a reopened
// report otherwise just reads "Pending", with no hint it was ever approved.
export function lastReopen(report) {
  const hist = arr(report && report.admin_actions_history).filter(h => h && h.action === 'reopened')
  if (!hist.length) return null
  let best = null
  let bestT = -Infinity
  hist.forEach((h) => {
    const t = h.at ? new Date(h.at).getTime() : NaN
    const v = Number.isFinite(t) ? t : -Infinity
    if (best === null || v >= bestT) { best = h; bestT = v }
  })
  return best
}

// The out-of-week stamp, or null. Written ONCE at creation time by
// api/field-reports?action=create-adhoc — "outside the current payroll week" is
// only meaningful relative to the moment of creation, so it is READ here and
// never re-derived from today's date.
export function outOfWeekEntry(report) {
  return arr(report && report.admin_actions_history)
    .find(h => h && h.action === 'created_out_of_week') || null
}

const asx = {
  wrap: { margin: '0 0 16px' },
  note: {
    background: '#fff8f0', borderLeft: '4px solid #e8820c', color: '#92510a',
    padding: '12px 16px', borderRadius: '0 6px 6px 0', fontSize: 13, lineHeight: 1.5,
    margin: '0 0 10px',
  },
  // Deliberately RED, not the amber a reopen uses: a late payroll entry is a
  // different kind of problem and must not read as "this was reopened".
  lateNote: {
    background: '#fdecec', borderLeft: '4px solid #d9534f', color: '#a12622',
    padding: '12px 16px', borderRadius: '0 6px 6px 0', fontSize: 13, lineHeight: 1.5,
    margin: '0 0 10px',
  },
  summary: { cursor: 'pointer', color: '#1e5b8f', fontSize: 13, fontWeight: 600 },
  list: { listStyle: 'none', margin: '8px 0 0', padding: 0, borderTop: '1px solid #eee' },
  li: { padding: '6px 0', borderBottom: '1px solid #f1f1f1', fontSize: 12.5, color: '#444', lineHeight: 1.5 },
  when: { color: '#777', whiteSpace: 'nowrap' },
  label: { fontWeight: 600, color: '#333' },
  who: { color: '#333' },
  detail: { color: '#888' },
  sep: { color: '#ccc', padding: '0 6px' },
}

export default function AuditTrail({ report }) {
  const trail = buildAuditTrail(report)
  if (!trail.length) return null
  const reopen = lastReopen(report)
  const late = outOfWeekEntry(report)

  return (
    <div style={asx.wrap}>
      {late && (
        <div style={asx.lateNote}>
          &#9888; <strong>Entered for {clean(late.report_date) || 'a past date'}</strong>, outside the
          payroll week in effect when it was created
          {clean(late.payroll_week_start) && clean(late.payroll_week_end)
            ? ` (${clean(late.payroll_week_start)}–${clean(late.payroll_week_end)})` : ''}.
          {' '}Check that week&#8217;s payroll before approving.
        </div>
      )}
      {reopen && (
        <div style={asx.note}>
          &#8617; <strong>Approval reopened</strong>{reopen.at ? ` ${fmtWhen(reopen.at)}` : ''}
          {reopen.by ? ` by ${reopen.by}` : ''}
          {reopen.previous_approver
            ? <> &mdash; previously approved by {reopen.previous_approver}
                {reopen.previous_approver_role ? ` (${reopen.previous_approver_role})` : ''}</>
            : null}
        </div>
      )}
      <details open={!!reopen}>
        <summary style={asx.summary}>History &mdash; {trail.length} event{trail.length === 1 ? '' : 's'}</summary>
        <ol style={asx.list}>
          {trail.map((e, i) => (
            <li key={i} style={asx.li}>
              <span style={asx.when}>{e.at ? fmtWhen(e.at) : '—'}</span>
              <span style={asx.sep}>&middot;</span>
              <span style={asx.label}>{e.label}</span>
              {e.who ? <><span style={asx.sep}>&middot;</span><span style={asx.who}>{e.who}</span></> : null}
              {e.detail ? <><span style={asx.sep}>&middot;</span><span style={asx.detail}>{e.detail}</span></> : null}
            </li>
          ))}
        </ol>
      </details>
    </div>
  )
}
